import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { useLocalizedPath, SUPPORTED_LANGS } from '@/hooks/useLanguage';

export default function Navbar() {
  const { t } = useTranslation();
  const localize = useLocalizedPath();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCats, setMobileCats] = useState(false);
  const location = useLocation();

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileCats(false);
  }, [location.pathname]);

  const segments = location.pathname.split('/').filter(Boolean);
  // Home is only "/" or "/:lang" (e.g. /pl). Article pages like /usfans-review
  // are a single segment too, so we must check that the segment is a language.
  const isHome =
    segments.length === 0 || (segments.length === 1 && SUPPORTED_LANGS.includes(segments[0]));
  // Over the dark hero image, the navbar uses light text. Elsewhere it uses dark text.
  const overlay = isHome && !scrolled;

  const logoColor = overlay ? 'text-background-50' : 'text-foreground-950';
  const linkColor = overlay
    ? 'text-background-50/90 hover:text-white'
    : 'text-foreground-700 hover:text-primary-600';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        overlay
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200 shadow-none'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
        <Link to={localize('/')} className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-grid-fill text-lg"></i>
          </span>
          <span className={`font-heading text-xl font-semibold ${logoColor}`}>USFansLink</span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          <Link to={localize('/')} className={`text-sm font-semibold transition-colors whitespace-nowrap ${linkColor}`}>
            {t('nav.home')}
          </Link>
          {isHome && (
            <a href="#categories" className={`text-sm font-semibold transition-colors whitespace-nowrap ${linkColor}`}>
              {t('nav.categories')}
            </a>
          )}
          {isHome && (
            <a href="#faq" className={`text-sm font-semibold transition-colors whitespace-nowrap ${linkColor}`}>
              {t('nav.faq')}
            </a>
          )}
          <Link
            to={localize('/usfans-spreadsheet-guide')}
            className={`text-sm font-semibold transition-colors whitespace-nowrap ${linkColor}`}
          >
            {t('nav.guide')}
          </Link>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className={`text-sm font-semibold transition-colors whitespace-nowrap ${linkColor}`}
          >
            {t('nav.shopping')}
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSelector />
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap md:inline-flex"
          >
            {t('nav.cta')}
          </a>
          <button
            className={`flex h-10 w-10 items-center justify-center rounded-lg ${overlay ? 'text-background-50' : 'text-foreground-800'} md:hidden`}
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-1">
            <Link
              to={localize('/')}
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
              {t('nav.home')}
            </Link>

            <button
              onClick={() => setMobileCats((o) => !o)}
              className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                {t('nav.categories')}
              </span>
              <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-foreground-500 transition-transform ${mobileCats ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCats && (
              <div className="grid grid-cols-2 gap-1.5 px-1 pb-1 pt-1">
                {categories.map((c) => (
                  <a
                    key={c.id}
                    href={c.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  >
                    <img src={c.image} alt={t(`cat.${c.id}`)} className="h-7 w-7 rounded object-cover object-top" />
                    <span className="truncate">{t(`cat.${c.id}`)}</span>
                  </a>
                ))}
              </div>
            )}

            {isHome && (
              <a href="#faq" className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100">
                <i className="ri-question-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                {t('nav.faq')}
              </a>
            )}

            <Link
              to={localize('/usfans-spreadsheet-guide')}
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-book-open-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
              {t('nav.guide')}
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
              {t('nav.shopping')}
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 inline-flex items-center justify-center rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50"
            >
              {t('nav.cta')}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}